import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, roleHome } from "../lib/auth";
import { Brand, Spinner } from "../components/Brand";
import { useSEO } from "../lib/seo";

export default function Register() {
    useSEO({
        title: "Create your free Luxurio Edge account — Book home services in UAE",
        description: "Sign up in 30 seconds. Book home services in Dubai, Abu Dhabi, Sharjah and Ajman with vetted technicians and transparent AED pricing. Free wallet & referral rewards.",
    });
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        password: "",
        role: "customer",
        referral_code: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const { register } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        const ref = searchParams.get("ref");
        if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
    }, [searchParams]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const payload = { ...form };
            if (!payload.referral_code) delete payload.referral_code;
            const u = await register(payload);
            const nextUrl = searchParams.get("next");
            const safeNext = nextUrl && nextUrl.startsWith("/") && !nextUrl.startsWith("//")
                ? nextUrl
                : null;
            nav(safeNext || roleHome(u.role), { replace: true });
        } catch (e) {
            setErr(e.message);
        } finally {
            setBusy(false);
        }
    };

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6 py-12">
            <div className="w-full max-w-lg">
                <Link to="/" className="inline-block mb-10">
                    <Brand />
                </Link>
                <div className="lx-label text-[#B2912A] mb-3">Create account</div>
                <h2 className="font-serif text-4xl text-[#180A21] mb-8">
                    Begin your <span className="italic">Luxurio</span> journey.
                </h2>

                <form onSubmit={submit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Full name" testid="reg-name">
                            <input
                                data-testid="reg-name-input"
                                required
                                value={form.name}
                                onChange={upd("name")}
                                className="lx-input"
                            />
                        </Field>
                        <Field label="Phone">
                            <input
                                data-testid="reg-phone-input"
                                value={form.phone}
                                onChange={upd("phone")}
                                className="lx-input"
                                placeholder="+971 …"
                            />
                        </Field>
                    </div>
                    <Field label="Email">
                        <input
                            data-testid="reg-email-input"
                            type="email"
                            required
                            value={form.email}
                            onChange={upd("email")}
                            className="lx-input"
                        />
                    </Field>
                    <Field label="Password">
                        <input
                            data-testid="reg-password-input"
                            type="password"
                            required
                            minLength={6}
                            value={form.password}
                            onChange={upd("password")}
                            className="lx-input"
                            placeholder="At least 6 characters"
                        />
                    </Field>
                    <Field label="I am a">
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                ["customer", "Customer"],
                                ["technician", "Technician"],
                            ].map(([v, l]) => (
                                <button
                                    key={v}
                                    type="button"
                                    data-testid={`reg-role-${v}`}
                                    onClick={() => setForm({ ...form, role: v })}
                                    className={`py-3 border transition text-sm font-medium ${
                                        form.role === v
                                            ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#180A21]"
                                            : "border-[#E5E2DC] text-[#555] hover:border-[#D4AF37]/50"
                                    }`}
                                >
                                    {l}
                                </button>
                            ))}
                        </div>
                    </Field>

                    {form.role === "customer" && (
                        <Field label="Referral code (optional)">
                            <input
                                data-testid="reg-ref-input"
                                value={form.referral_code}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        referral_code: e.target.value.toUpperCase(),
                                    })
                                }
                                className="lx-input"
                                placeholder="Get 50% off your first membership"
                            />
                            {form.referral_code && (
                                <div className="text-xs text-[#D4AF37] mt-2">
                                    ★ Referral code applied — 50% off your first month
                                </div>
                            )}
                        </Field>
                    )}

                    {err && (
                        <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">
                            {err}
                        </div>
                    )}

                    <button
                        data-testid="reg-submit-btn"
                        disabled={busy}
                        type="submit"
                        className="lx-btn-primary w-full py-3.5 font-medium tracking-wide disabled:opacity-50 inline-flex items-center justify-center gap-2 mt-2"
                    >
                        {busy ? <Spinner /> : "Create account →"}
                    </button>
                </form>

                <style>{`.lx-input{width:100%;padding:.75rem 1rem;background:white;border:1px solid #E5E2DC;outline:none;transition:all 200ms ease}.lx-input:focus{border-color:#D4AF37;box-shadow:0 0 0 1px #D4AF37}`}</style>

                <p className="mt-8 text-sm text-[#555]">
                    Already have an account?{" "}
                    <Link
                        data-testid="reg-to-login"
                        to={searchParams.get("next") ? `/login?next=${encodeURIComponent(searchParams.get("next"))}` : "/login"}
                        className="text-[#180A21] underline underline-offset-4 decoration-[#D4AF37] hover:text-[#D4AF37]"
                    >
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="lx-label text-[#555] mb-2 block">{label}</span>
            {children}
        </label>
    );
}
